import { useEffect, useMemo, useState } from 'react'
import {
  ENOCH_PARTS,
  ENOCH_PORTAL,
  ENOCH_VERSE,
  SEASON_LEADERS,
  YEAR_START,
  feastsOn,
  fmtTime,
  loadDays,
  loadFeasts,
  ordinal,
  reckonToday,
  setYearStart,
  yearStart,
} from '../calendar'
import type { Feast, PublishedDay, Today, YearStartOption } from '../calendar'
import { downloadIcs } from '../ical'
import { useToast } from '../shell/Toast'
import { LocationSheet } from './LocationSheet'

const WORDS = [
  'first', 'second', 'third', 'fourth', 'fifth', 'sixth',
  'seventh', 'eighth', 'ninth', 'tenth', 'eleventh', 'twelfth',
]

const fmtG = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

/**
 * The biblical calendar page, laid out like the Torahly web calendar: a month grid with the
 * Sabbath as its own column, the parts of day and night beside it, and a tab for the feasts.
 *
 * It reads the same published year, the same feasts file and the same sunset source as Torahly's
 * (see calendar.ts), and generates nothing. The Luminaries tab on Torahly's page is the sun dial
 * and is not carried over.
 */
export function CalendarPage() {
  const toast = useToast()
  const [days, setDays] = useState<PublishedDay[] | null>(null)
  const [feasts, setFeasts] = useState<Feast[]>([])
  const [today, setToday] = useState<Today | null>(null)
  const [month, setMonth] = useState<number | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [option, setOption] = useState<YearStartOption>(yearStart())
  const [tab, setTab] = useState<'month' | 'feasts'>('month')
  const [locOpen, setLocOpen] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let alive = true
    setDays(null)
    Promise.all([loadDays(option), loadFeasts()])
      .then(async ([d, f]) => {
        if (!alive) return
        setDays(d)
        setFeasts(f)
        const t = await reckonToday(d)
        if (!alive) return
        setToday(t)
        setMonth((m) => m ?? t?.day.m ?? d[0]?.m ?? 1)
        setSelected((s) => s ?? t?.day.d ?? null)
      })
      .catch(() => alive && setDays([]))
    return () => {
      alive = false
    }
  }, [option, tick])

  // The 364 days of the year today falls in.
  const year = useMemo(() => {
    if (!days || days.length === 0) return []
    const anchor = today?.day ?? days[days.length - 1]!
    const idx = days.findIndex((d) => d.d === anchor.d)
    const start = Math.max(0, idx - (anchor.doy - 1))
    return days.slice(start, start + 364)
  }, [days, today])
  const monthDays = useMemo(() => year.filter((d) => d.m === month), [year, month])

  if (days === null) return <p className="quiet-center">One moment</p>
  if (year.length === 0) {
    return (
      <div className="card cal-empty">
        The calendar for this year could not be reached. Try again in a moment.
      </div>
    )
  }

  const m = month ?? 1
  const parts = ENOCH_PARTS[m] ?? [9, 9]
  const monthFeasts = feasts.filter((f) => f.month === m)

  const selectedDay = monthDays.find((x) => x.d === selected) ?? today?.day
  const selectedNotes =
    selectedDay && monthDays.some((x) => x.d === selectedDay.d)
      ? [
          selectedDay.w === 7
            ? 'The Sabbath'
            : feastsOn(feasts, selectedDay).some((f) => f.sabbathDays.includes(selectedDay.dy))
              ? 'A Sabbath'
              : '',
          selectedDay.dy === 31 ? 'Intercalary Day' : '',
          selectedDay.dy === 1 && [1, 4, 7, 10].includes(selectedDay.m) ? 'Day of Remembrance' : '',
          ...feastsOn(feasts, selectedDay).map((f) => f.name),
        ].filter(Boolean)
      : null

  return (
    <div className="cal">
      <header className="cal-head">
        <div>
          <div className="cal-kicker">
            <span className="chip chip-gold">364-day solar year</span>
            <span className="cal-dim">1 Enoch 72 and Jubilees 6</span>
          </div>
          <h1 className="cal-title">Biblical Calendar</h1>
          <p className="cal-where">
            {today?.sun && (
              <>
                <span>
                  Sunset today <strong>{fmtTime(today.sun.sunset, today.location)}</strong>
                </span>
                <span className="cal-dim">·</span>
              </>
            )}
            <span>
              <i className="ph-bold ph-map-pin" aria-hidden="true" /> {today?.location.name ?? 'Jerusalem'}
            </span>
            <button className="cal-link" type="button" onClick={() => setLocOpen(true)}>
              Change location
            </button>
          </p>
        </div>

        <div className="cal-tools">
          <button
            className="btn-outline"
            type="button"
            disabled={days.length === 0}
            onClick={() => downloadIcs(days, feasts, option)}
            title="Every feast and Sabbath of the published year as all-day events, for Google Calendar, Outlook or a phone"
          >
            <i className="ph-bold ph-calendar-plus" aria-hidden="true" /> Add to my calendar (.ics)
          </button>
          <div className="seg" role="group" aria-label="Year start">
            {(['WEDNESDAY', 'YAHS_ENOCH'] as YearStartOption[]).map((o) => (
              <button
                key={o}
                type="button"
                className={option === o ? 'seg-on' : ''}
                title={YEAR_START[o].detail}
                onClick={() => {
                  setYearStart(o)
                  setOption(o)
                }}
              >
                {YEAR_START[o].label}
              </button>
            ))}
          </div>
          <div className="seg" role="group" aria-label="View">
            <button type="button" className={tab === 'month' ? 'seg-on' : ''} onClick={() => setTab('month')}>
              Month
            </button>
            <button type="button" className={tab === 'feasts' ? 'seg-on' : ''} onClick={() => setTab('feasts')}>
              Feasts and seasons
            </button>
          </div>
        </div>
      </header>

      {tab === 'month' && (
        <div className="cal-body">
          <section className="card cal-grid-card">
            <div className="cal-monthbar">
              <button
                className="icon-btn"
                type="button"
                onClick={() => setMonth(Math.max(1, m - 1))}
                disabled={m === 1}
                aria-label="Previous month"
              >
                <i className="ph-bold ph-caret-left" aria-hidden="true" />
              </button>
              <div className="cal-monthname">
                <h2>
                  {ordinal(m)} month <span className="chip chip-gold">{monthDays.length} days</span>
                </h2>
                <div className="cal-dim cal-mono">
                  {monthDays[0]?.d} to {monthDays[monthDays.length - 1]?.d}
                </div>
              </div>
              <button
                className="icon-btn"
                type="button"
                onClick={() => setMonth(Math.min(12, m + 1))}
                disabled={m === 12}
                aria-label="Next month"
              >
                <i className="ph-bold ph-caret-right" aria-hidden="true" />
              </button>
            </div>

            <div className="cal-weekdays">
              {['1st', '2nd', '3rd', '4th', '5th', '6th'].map((w) => (
                <div key={w}>{w}</div>
              ))}
              <div className="is-sabbath">Sabbath</div>
            </div>
            <div className="cal-days" role="grid">
              {monthDays[0] &&
                Array.from({ length: monthDays[0].w - 1 }).map((_, i) => <div key={`pad${i}`} />)}
              {monthDays.map((d) => {
                const fs = feastsOn(feasts, d)
                // A feast's holy convocations (Leviticus 23) are sabbaths too, drawn like the weekly one.
                const isSabbath = d.w === 7 || fs.some((f) => f.sabbathDays.includes(d.dy))
                const isSelected = selected === d.d
                const isToday = today?.day.d === d.d
                // Day 31 closes each quarter (1 Enoch 72:13, 19, 25, 31).
                const isIntercalary = d.dy === 31
                // The 1st of the 1st, 4th, 7th and 10th month (Jubilees 6:23-31).
                const isRemembrance = d.dy === 1 && [1, 4, 7, 10].includes(d.m)
                const leader = isRemembrance ? SEASON_LEADERS[d.m] : undefined
                const kind = isSelected
                  ? 'is-selected'
                  : isSabbath
                    ? 'is-sabbath'
                    : isIntercalary
                      ? 'is-inter'
                      : isRemembrance
                        ? 'is-remember'
                        : ''
                return (
                  <button
                    key={d.d}
                    type="button"
                    role="gridcell"
                    aria-selected={isSelected}
                    className={`cal-day ${kind} ${isToday ? 'is-today' : ''}`}
                    onClick={() => {
                      setSelected(d.d)
                      toast(
                        `Day ${d.dy} (${fmtG(d.d)})${d.w === 7 ? ', the Sabbath' : isSabbath ? ', a Sabbath' : ''}${
                          fs.length ? `, ${fs.map((f) => f.name).join(', ')}` : ''
                        }`,
                      )
                    }}
                  >
                    <div className="cal-day-top">
                      <span className="cal-num">{d.dy}</span>
                      {isSabbath && <span className="cal-flag">{d.w === 7 ? 'Shabbat' : 'Sabbath'}</span>}
                      {leader && !isSabbath && <span className="cal-leader">{leader.name}</span>}
                    </div>
                    {isIntercalary && <div className="cal-note is-inter">Intercalary Day</div>}
                    {isRemembrance && <div className="cal-note is-remember">Day of Remembrance</div>}
                    {fs.length > 0 && <div className="cal-note is-feast">{fs.map((f) => f.name).join(', ')}</div>}
                    <div className="cal-g">
                      {fmtG(d.d)}
                      {isToday && <span className="cal-today-word"> · today</span>}
                    </div>
                  </button>
                )
              })}
            </div>

            {selectedNotes && (
              <div className="cal-picked" aria-live="polite">
                <div className="cal-mono cal-dim">
                  Day {selectedDay!.dy} · {fmtG(selectedDay!.d)}
                  {today?.day.d === selectedDay!.d ? ' · today' : ''}
                </div>
                {selectedNotes.length > 0 ? (
                  <ul>
                    {selectedNotes.map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="cal-dim">An ordinary day.</p>
                )}
              </div>
            )}

            <p className="cal-foot">
              <i className="ph-bold ph-info" aria-hidden="true" />
              <span>
                Each biblical day begins at sunset on the evening before the Gregorian date shown,
                and runs to the next sunset. The <strong>Sabbath column</strong> is the holy seventh
                day (Exodus 20:8-11). Day 31 closes each quarter as the{' '}
                <strong>Intercalary Day</strong> (1 Enoch 72:13, 19, 25, 31), and the 1st of the 1st,
                4th, 7th and 10th months is a <strong>Day of Remembrance</strong> (Jubilees 6:23-31).
                On that day one of the four <strong>leaders of the seasons</strong> rises and rules
                the 91 days that follow: Melkejal in spring, Helemmelek in summer, Melejal in autumn,
                Narel in winter (1 Enoch 82:11-18).
              </span>
            </p>
          </section>

          <aside className="cal-side">
            <section className="card">
              <h3 className="card-title">
                <i className="ph-bold ph-sun" aria-hidden="true" /> Parts of day and night
              </h3>
              <div className="cal-parts">
                <div className="is-day">
                  <b>{parts[0]}</b>
                  <span>Day parts</span>
                </div>
                <div>
                  <b>{parts[1]}</b>
                  <span>Night parts</span>
                </div>
              </div>
              <div className="cal-bar" aria-hidden="true">
                <i style={{ width: `${(parts[0] / 18) * 100}%` }} />
              </div>
              <p className="cal-small">
                In the {WORDS[m - 1]} month the sun rises through the {ENOCH_PORTAL[m]} portal:{' '}
                {parts[0]} parts day and {parts[1]} parts night ({ENOCH_VERSE[m]}).
              </p>
            </section>

            <section className="card">
              <div className="cal-row">
                <h3 className="card-title">
                  <i className="ph-bold ph-calendar-heart" aria-hidden="true" /> This month's feasts
                </h3>
                <span className="chip">
                  {monthFeasts.length} {monthFeasts.length === 1 ? 'feast' : 'feasts'}
                </span>
              </div>
              {monthFeasts.length === 0 && (
                <p className="cal-small">No appointed feast falls in the {ordinal(m)} month.</p>
              )}
              {monthFeasts.map((f) => (
                <div className="cal-feast" key={f.id}>
                  <div>
                    <span>{f.name}</span>
                    <span className="cal-mono cal-gold">
                      day {f.startDay}
                      {f.endDay !== f.startDay ? `-${f.endDay}` : ''}
                    </span>
                  </div>
                  <div className="cal-mono cal-dim">{f.verses.join(' · ')}</div>
                </div>
              ))}
            </section>

            <section className="card">
              <h3 className="card-title">
                <i className="ph-bold ph-compass" aria-hidden="true" /> Two readings, one year
              </h3>
              <p className="cal-small">
                It's the same 364-day year either way. Enoch lays it out, Jubilees charges us to
                keep it (1 Enoch 72, Jubilees 6:32-38): 52 weeks exactly, beginning on the fourth day
                of the week, when the luminaries were set (Genesis 1:14-19). They part on one point:
                which Wednesday near the spring equinox is Day 1.
              </p>
              <dl className="cal-readings">
                {(['WEDNESDAY', 'YAHS_ENOCH'] as YearStartOption[]).map((o) => (
                  <div key={o}>
                    <dt className={option === o ? 'is-on' : ''}>{YEAR_START[o].label}</dt>
                    <dd>{YEAR_START[o].detail}</dd>
                  </div>
                ))}
              </dl>
            </section>
          </aside>
        </div>
      )}

      {tab === 'feasts' && (
        <section className="card">
          <h2 className="section-title">Biblical appointed times (Moedim), 364-day cycle</h2>
          <p className="section-lede">
            Under the 364-day calendar the feasts fall on the same weekday every year and never
            drift.
          </p>
          <div className="feature-grid">
            {feasts.map((f) => {
              const start = year.find((d) => d.m === f.month && d.dy === f.startDay)
              const inDays =
                start && today
                  ? Math.round(
                      (new Date(start.d).getTime() - new Date(today.day.d).getTime()) / 86400000,
                    )
                  : null
              return (
                <div className="feature" key={f.id}>
                  <div className="cal-row">
                    <h4>{f.name}</h4>
                    <span className="chip chip-gold">
                      Month {f.month} : Day {f.startDay}
                      {f.endDay !== f.startDay ? `-${f.endDay}` : ''}
                    </span>
                  </div>
                  <div className="cal-mono cal-dim">
                    {start ? `${WORDS[start.w - 1]} day` : ''}
                    {inDays !== null
                      ? ` · ${inDays === 0 ? 'today' : inDays > 0 ? `in ${inDays} days` : `${-inDays} days ago`}`
                      : ''}{' '}
                    · {f.verses[0]}
                  </div>
                  <p>{f.description}</p>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {locOpen && (
        <LocationSheet
          onClose={() => {
            setLocOpen(false)
            setTick((n) => n + 1)
          }}
        />
      )}
    </div>
  )
}
